import React, { useContext, useRef, useMemo } from 'react';
import type { NetworkQuality } from '@zoom/videosdk';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { Participants } from 'index-types';
import ZoomContext from 'zoom/context/zoom-context';
import type { AdvancedFeatureSwitch } from 'shared/types/video.interface';
import { Typography, Stack, Theme } from '@mui/material';
import { CustomMuiIcon } from 'assets/images/icons/customMuiIcons';
import { IDebateData, ERoundStatuses } from 'shared/types/debates.interface';
import { EAvatarTypes } from 'shared/types/card.interface';
import { useGetProfileQuery } from 'redux/api/user.api';
import { RoundProgress } from 'components/RoundProgress/RoundProgress';
import { Avatar } from 'components/Avatar/Avatar';

interface AvatarProps {
  participant: Participants;
  debate: IDebateData;
  style?: { [key: string]: string };
  isActive: boolean;
  volume?: number;
  isUserCameraControlled?: boolean;
  advancedFeature?: AdvancedFeatureSwitch;
  networkQuality?: NetworkQuality;
  onAdvancedFeatureToggle?: (userId: number, key: string) => void;
  setLocalVolume?: (userId: number, volume: number) => void;
}

const networkQualityIcons = ['bad', 'bad', 'normal', 'good', 'good', 'good'];
const ZoomAvatar = (props: AvatarProps) => {
  const { participant, debate, style, isActive, networkQuality } = props;
  const { displayName, audio, muted, bVideoOn, userId } = participant;
  const avatarRef = useRef(null);
  const zmClient = useContext(ZoomContext);
  const { data: profileData } = useGetProfileQuery();

  const currentParticipant = useMemo(() => {
    const isMe = userId === zmClient.getSessionInfo().userId;
    if (isMe) {
      return profileData.id === debate.challenge.initiator.id
        ? debate.challenge.initiator
        : debate.challenge.opponent;
    }
    return profileData.id !== debate.challenge.initiator.id
      ? debate.challenge.initiator
      : debate.challenge.opponent;
  }, [debate.challenge.initiator, debate.challenge.opponent, profileData.id, userId, zmClient]);

  const currentRound = useMemo(
    () => debate?.rounds.find((round) => round.status === ERoundStatuses.Active) ?? null,
    [debate?.rounds],
  );

  const lastRound = useMemo(
    () =>
      debate?.rounds
        .filter(
          (round) =>
            round.status === ERoundStatuses.Completed && round.owner === currentParticipant.id,
        )
        .sort((a, b) => b.global_round_number - a.global_round_number)[0] ?? null,
    [debate?.rounds, currentParticipant.id],
  );

  return (
    <Stack
      direction="row"
      sx={(theme: Theme) => ({
        backgroundColor: bVideoOn ? 'transparent' : 'secondary.main',
        position: 'absolute',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: 'visible',
        borderRadius: '8px',
        border: isActive
          ? {
              md: `4px solid ${theme.palette.myColors.green}`,
              xs: `2px solid ${theme.palette.myColors.green}`,
            }
          : {
              md: `4px solid ${theme.palette.primary.contrastText}`,
              xs: `2px solid ${theme.palette.primary.contrastText}`,
            },
        outline: `4px solid ${theme.palette.primary.contrastText}`,
        ...style,
      })}
      ref={avatarRef}
    >
      <Stack
        direction="row"
        sx={(theme: Theme) => ({
          position: 'absolute',
          left: '12px',
          bottom: '12px',
          alignItems: 'center',
          backgroundColor: `${theme.palette.primary.contrastText}CC`,
          borderRadius: '4px',
          columnGap: '4px',
          p: '4px 8px',
        })}
      >
        <Typography variant="body2" color="secondary.main">
          {displayName}
        </Typography>
        {networkQuality && (
          <CustomMuiIcon
            type={`icon-network-${
              networkQualityIcons[
                Math.min(
                  networkQuality?.downlink ?? Number.MAX_VALUE,
                  networkQuality?.uplink ?? Number.MAX_VALUE,
                )
              ]
            }`}
            sx={{ color: 'secondary.main', fontSize: { lg: '20px', md: '16px', xs: '14px' } }}
          />
        )}
        {audio === 'computer' && muted && (
          <VolumeOffIcon
            sx={{
              fontSize: '20px',
              color: 'secondary.main',
            }}
          />
        )}
      </Stack>
      {!bVideoOn && (
        <Avatar
          type={EAvatarTypes.FullCard}
          isFullCard
          firstName={currentParticipant.first_name}
          lastName={currentParticipant.last_name}
        />
      )}
      <RoundProgress
        currentRound={currentRound}
        totalRoundsNumber={debate.challenge.debate_settings.rounds_per_participant}
        isParticipantActive={currentParticipant.id === currentRound?.owner}
        lastRound={lastRound}
        position="absolute"
        left="0"
        sx={{ bottom: { md: '-40px', xs: '-26px' } }}
      />
    </Stack>
  );
};

export default ZoomAvatar;
